// src/oura/team/teamApi.ts
//
// The merchant's team: who can sign in to their Oura account. A thin layer
// over the workspace-members calls the recruiter Settings page already uses
// (GET /companies/permissions, POST /companies/invite, DELETE
// /companies/member). The backend already sends a merchant's colleague Oura's
// own invitation, valid 14 days (ourteam-backend#5965), so nothing here is new
// on the server.
//
// `VITE_OURA_TEAM_SAMPLE=true` (local preview only, never production) answers
// from a sample team instead of the API, so the screens can be seen without a
// backend.

import { getCompanyPermissions, inviteCompanyPermissions, removeCompanyPermissions } from "@service/user";
import { authResendCode, checkHRDomain, registerHRotp, verifyHREmail } from "@service/auth";
import type { UserPermissionItem } from "@type/company";
import { setCookie } from "@util/cookie";

export interface TeamMember {
    /** The user id once joined; null while only invited. */
    userId: string | null;
    name: string;
    email: string;
    owner: boolean;
    invited: boolean;
    /** Joined on, or invited on. ISO. */
    since: string | null;
}

/** Oura has one tier for a merchant's colleague: full access ("write"). */
const OURA_PERMISSION = "write";

const sampleOn = import.meta.env.VITE_OURA_TEAM_SAMPLE === "true" && import.meta.env.VITE_APP_ENV !== "production";

let sample: TeamMember[] = [
    { userId: "u-1", name: "Mei Lin Tan", email: "meilin@nyonyatable.my", owner: true, invited: false, since: "2026-10-06T09:00:00Z" },
    { userId: "u-2", name: "Daniel Lee", email: "daniel@nyonyatable.my", owner: false, invited: false, since: "2026-10-09T09:00:00Z" },
    { userId: null, name: "", email: "aisyah@nyonyatable.my", owner: false, invited: true, since: "2026-10-17T09:00:00Z" },
];

const wait = () => new Promise((r) => setTimeout(r, 400));

function toMember(row: UserPermissionItem): TeamMember {
    const invited = row.permission === "invited";
    return {
        userId: invited ? null : row.user_id,
        name: [row.first_name, row.last_name].filter(Boolean).join(" ").trim(),
        email: row.email,
        owner: row.permission === "owner",
        invited,
        since: row.joined_at || null,
    };
}

/** Owner first, then people who joined, then invites; each by name or email. */
function order(list: TeamMember[]): TeamMember[] {
    const rank = (m: TeamMember) => (m.owner ? 0 : m.invited ? 2 : 1);
    return [...list].sort((a, b) => rank(a) - rank(b) || (a.name || a.email).localeCompare(b.name || b.email));
}

export async function listTeam(): Promise<TeamMember[]> {
    if (sampleOn) {
        await wait();
        return order(sample);
    }
    const resp = await getCompanyPermissions({ page: 1, limit: 100 });
    const rows: UserPermissionItem[] = resp?.data?.data?.members ?? [];
    return order(rows.map(toMember));
}

/** Sends the Oura invitation. Resending is the same call: it starts a fresh 14 days. */
export async function inviteToTeam(email: string): Promise<void> {
    if (sampleOn) {
        await wait();
        const at = sample.findIndex((m) => m.email === email);
        const row: TeamMember = { userId: null, name: "", email, owner: false, invited: true, since: new Date().toISOString() };
        sample = at >= 0 ? sample.map((m, i) => (i === at ? row : m)) : [...sample, row];
        return;
    }
    await inviteCompanyPermissions({ inviteMember: [{ email, permission: OURA_PERMISSION }] });
}

/** Takes away a colleague's login, or cancels an invitation. Only the owner may. */
export async function removeFromTeam(member: TeamMember): Promise<void> {
    if (sampleOn) {
        await wait();
        sample = sample.filter((m) => m.email !== member.email);
        return;
    }
    await removeCompanyPermissions(member.invited ? { user_id: null, email: member.email } : { user_id: member.userId, email: null });
}

/** The server's own sentence for a refusal, when it gave one. */
export function teamError(err: unknown, fallback: string): string {
    const data = (err as { response?: { data?: { message?: string; errors?: string[] } } })?.response?.data;
    return data?.message || data?.errors?.[0] || fallback;
}

// ---- The colleague's side: accepting the invitation ----------------------
//
// The same three calls the recruiter sign-up makes for an invited person
// (employer-onboarding.tsx): find the workspace the invite is for, create the
// login, then confirm it with the 6-digit code the backend emails. The
// backend gives the new login the merchant role (oura_role_for_company) and
// refuses an invite older than 14 days (#5965).

export interface JoinInvite {
    email: string;
    businessName: string;
    inviterName: string;
}

/** What the invite link carries: base64 JSON with the email, inviter and company. */
export function readInviteToken(token: string): JoinInvite | null {
    try {
        const raw = JSON.parse(atob(token.replace(/-/g, "+").replace(/_/g, "/")));
        if (!raw?.email) return null;
        return { email: String(raw.email), businessName: String(raw.company_name || ""), inviterName: String(raw.inviter_name || "") };
    } catch {
        return null;
    }
}

/** A sample invite for the local preview, when the page is opened with no token. */
export const SAMPLE_INVITE: JoinInvite | null = sampleOn
    ? { email: "aisyah@nyonyatable.my", businessName: "Nyonya Table", inviterName: "Mei Lin" }
    : null;

export class JoinRefused extends Error {}

const INVITE_GONE = "This invite is no longer open. Ask your colleague to send you a new one.";

/** Creates the login. The backend then emails a 6-digit code to confirm it. */
export async function createJoinLogin(input: { email: string; firstName: string; lastName: string; password: string }): Promise<void> {
    if (sampleOn) {
        await wait();
        return;
    }
    let companyId: string;
    try {
        const resp = await checkHRDomain(input.email);
        const data = resp?.data?.data;
        if (!data?.domain_exists || !data?.company_id) throw new JoinRefused(INVITE_GONE);
        companyId = data.company_id;
    } catch (err) {
        if (err instanceof JoinRefused) throw err;
        throw new JoinRefused(teamError(err, "Something went wrong. Try again."));
    }
    try {
        const resp = await registerHRotp({
            work_email: input.email,
            password: input.password,
            confirm_password: input.password,
            first_name: input.firstName,
            last_name: input.lastName,
            company_id: companyId,
        });
        // The endpoint answers 202 when it sent the code; anything else carries its reason.
        if (resp.status !== 202) throw new JoinRefused(resp?.data?.message || "Something went wrong. Try again.");
    } catch (err) {
        if (err instanceof JoinRefused) throw err;
        throw new JoinRefused(teamError(err, "Something went wrong. Try again."));
    }
}

/** Confirms the login with the emailed code; on success the person is signed in. */
export async function confirmJoinCode(email: string, code: string): Promise<void> {
    if (sampleOn) {
        await wait();
        if (code !== "123456") throw new JoinRefused("That code doesn't match. Check the email and try again.");
        return;
    }
    try {
        const resp = await verifyHREmail(email, code);
        setCookie("authToken", resp?.data?.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);
    } catch {
        throw new JoinRefused("That code doesn't match. Check the email and try again.");
    }
}

export async function resendJoinCode(email: string): Promise<void> {
    if (sampleOn) {
        await wait();
        return;
    }
    await authResendCode(email);
}
